<script lang="ts">
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Tabs from '../ui/Tabs.svelte';
  import LobbyChatTab from './LobbyChatTab.svelte';
  import LobbyRoster from './LobbyRoster.svelte';
  import { countUnreadAfter } from './lobby-chat';
  import { describeRoomConfig, rankRoomTotals } from './room-stats';
  import type { GameConfig } from '../../shared/config';
  import type { ChatMessage, PlayerView } from '../../shared/protocol';

  interface Props {
    config: GameConfig;
    players: readonly Readonly<PlayerView>[];
    meId: string;
    isHost: boolean;
    canKick: boolean;
    kickPlayer: (id: string) => void;
    resetScores: () => void;
    chat: readonly Readonly<ChatMessage>[];
    sendChat: (text: string) => boolean;
    connected: boolean;
  }
  let { config, players, meId, isHost, canKick, kickPlayer, resetScores, chat, sendChat, connected }: Props =
    $props();

  type DetailsTab = 'players' | 'scores' | 'chat';
  let tab = $state<DetailsTab>('players');
  let lastReadId = $state<string | null>(null);
  const unread = $derived(countUnreadAfter(chat, lastReadId));
  /** Solo rooms seat one; there is nobody to talk to. */
  const showChat = $derived(config.maxPlayers > 1);
  const items = $derived<readonly { value: DetailsTab; label: string }[]>([
    { value: 'players', label: `Players (${players.length})` },
    { value: 'scores', label: 'Scores' },
    ...(showChat ? [{ value: 'chat' as const, label: unread ? `Chat (${unread})` : 'Chat' }] : []),
  ]);
  const summary = $derived(describeRoomConfig(config));
  const totals = $derived(rankRoomTotals(players));
  const hasTotals = $derived(players.some((player) => player.total > 0));

  $effect(() => {
    if (tab === 'chat' && chat.length) lastReadId = chat.at(-1)!.id;
  });
</script>

<Card class="lobby-details-card">
  <Tabs bind:value={tab} {items} label="Room details">
    {#snippet children(current)}
      {#if current === 'players'}
        <LobbyRoster {players} {meId} {canKick} {kickPlayer} />
      {:else if current === 'scores'}
        <p class="hint">{summary}</p>
        {#if hasTotals}
          <ul class="totals" aria-label="Room totals">
            {#each totals as row (row.id)}
              <li><span>{row.avatar} {row.name}</span><b>{row.total} pts</b></li>
            {/each}
          </ul>
        {:else}
          <p class="hint">No points yet — totals appear here after the first round.</p>
        {/if}
        {#if isHost && hasTotals}
          <div class="row tiny-actions">
            <Button variant="destructive" size="small" onclick={resetScores}>Reset Totals</Button>
          </div>
        {/if}
      {:else if showChat}
        <LobbyChatTab messages={chat} {meId} {sendChat} {connected} />
      {/if}
    {/snippet}
  </Tabs>
</Card>

<style>
  .tiny-actions {
    margin-top: 4px;
  }
  :global(.card.lobby-details-card) {
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
  }
  :global(.card.lobby-details-card .tabs-root) {
    flex: 1 1 auto;
    min-height: 0;
  }
  :global(.card.lobby-details-card .tab-panel:not([hidden])) {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .totals {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .totals li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    font-size: 14px;
  }
  .totals b {
    color: var(--theme-accent);
  }
</style>
